<template>
  <div class="style-cart-page container justify-content-center p-0 mt-3">
    <!-- Cart Item -->
    <div class="style-cart-item-margin">
      <div v-for="data in carts" :key="data.id" class="mb-4">
        <div class="style-product-container d-flex flex-row">
          <expandable-list
            class="flex-grow-1"
            :border="data.product.promotion"
            @click="viewItemDetail(data)">
            <expandable-list-header
              v-if="data.product.promotion"
              :content="getPromotionHeader(data.product)"/>
            <expandable-list-item
              :imgUrl="data.product.image"
              :name="data.product.name"
              :processingTime="data.processingTime"
              :description="data.product.description"
              :originalPrice="data.product.originalPrice"
              :salePrice="data.product.salePrice"
              :clickable="false" />
          </expandable-list>
        </div>
      </div>
    </div>

    <!-- Enter Remark -->
    <list-header title="Remarks" />

    <div class="p-3">
      <textarea
        class="form-control style-textarea"
        id="specialRequest"
        rows="5"
        placeholder="Special Request"
        v-model="remarks"
      />
    </div>

    <!-- Payment Summary -->
    <div>
      <list-header title="Payment" />

      <div
        class="p-4 style-payment-summary d-flex flex-row"
        style="line-height: 30px">
        <div class="d-flex flex-column text-start pe-3">
          <span class="scss-bold">Payment Method:</span>
          <span class="scss-text-light-color">Subtotal:</span>
          <span class="scss-text-light-color">Tax:</span>
          <span class="scss-bold">Total:</span>
        </div>
        <div class="d-flex flex-column text-end flex-grow-1">
          <!-- Select Payment Methods -->
          <div
            class="d-flex flex-row scss-click-effect justify-content-end scss-bold scss-text-blue"
            data-bs-toggle="modal"
            data-bs-target="#selectPaymentMethod">
            <div class="text-end me-1">
              {{ formatPaymentMethodName(selectedPaymentMethod) }}
            </div>
            <div class="d-flex flex-column" style="-webkit-text-stroke: 1px">
              <i class="bi bi-chevron-right align-self-center" />
            </div>
          </div>
          <div>
              {{
                `${branchCurrencySymbol} ${formatPrice(saleTicketData?.subtotal)}`
              }}
            </div>
            <div>
              {{ `${branchCurrencySymbol} ${formatPrice(saleTicketData?.tax)}` }}
            </div>
            <div class="scss-bold">
              {{
                `${branchCurrencySymbol} ${formatPrice(
                  saleTicketData?.taxedTotal
                )}`
              }}
            </div>
        </div>
      </div>
    </div>

    <!-- Check Out Button -->
    <div class="mt-2">
      <default-button
        title="Check Out"
        :disabled="selectedPaymentMethodId === null"
        @click="checkOutHandler"
      />
    </div>

    <!-- Modal to select payment method -->
    <div
      class="modal fade"
      id="selectPaymentMethod"
      tabindex="-1"
      aria-labelledby="exampleModalLabel"
      aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
              <h5 class="modal-title" id="exampleModalLabel">
                Select Payment Method
              </h5>
              <button
                type="button"
                class="close"
                data-bs-dismiss="modal"
                aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
          </div>
          <div class="modal-body p-0 py-2">
            <div v-if="paymentMethods.length">
              <radio-button-group
                :options="paymentMethods"
                :formatDisplayNameFunction="formatPaymentMethodName"
                v-model="selectedPaymentMethodId"
                data-bs-dismiss="modal" />
            </div>
            <div v-else>
              <item-not-found
                message="No payment methods found."
                style="margin-top: 0px !important"/>
            </div>
          </div>
          <div class="modal-footer">
            &nbsp;
          </div>
        </div>
      </div>
      <fiserv-form
        v-if="fiservPayment"
        :fiservUrl="saleTicketData.url ? saleTicketData.url : null"
        />
    </div>

    <!-- Navigation Bar -->
    <div class="row justify-content-center fixed-bottom">
      <nav-bar />
    </div>
  </div>
</template>

<script>
import {
  defineAsyncComponent,
  markRaw
} from "vue";
import { mapMutations, mapGetters, mapActions } from "vuex";

import moment from "moment";
import { useToast } from "vue-toastification";
import DefaultButton from "@/components/button/DefaultButton.vue";
import ExpandableList from "@/components/ExpandableList.vue";
import ExpandableListHeader from "@/components/ExpandableListHeader.vue";
import ExpandableListItem from "@/components/ExpandableListItem.vue";
import ItemNotFound from "@/components/ItemNotFound.vue";
import ListHeader from "@/components/ListHeader.vue";
import NavBar from "@/components/NavBar.vue";
import RadioButtonGroup from "@/components/RadioButtonGroup.vue";
import FiservForm from "@/views/Home/MakeAppointment/Fiserv/FiservForm.vue";
import {
  saleTicket,
  getSaleTicket,
} from "@/services/saleTicket";
import { formatPrice, getFormattedNumber } from "@/common/helpers/PriceHelper.js";
import {
  getPromotionHeader,
  getItemLine
} from "@/common/helpers/ProductHelper";
import { paymentGatewayProvider } from "@/common/constant/PaymentTypes";
import { stripePaymentMethod } from "@/common/constant/Stripe.js";
import { ticketStatusList } from "@/common/constant/TicketStatus";
import { openModal, closeModal } from "@/store/modal";

export default {
  name: "CheckOut",
  components: {
    DefaultButton,
    ExpandableList,
    ExpandableListHeader,
    ExpandableListItem,
    ItemNotFound,
    ListHeader,
    NavBar,
    RadioButtonGroup,
    FiservForm,
  },
  data() {
    return {
      selectedPaymentMethodId: null,
      remarks: "",
      saleTicketData: null,
      fiservPayment: false,
      loopPayment: null,
      carts: [],
    };
  },
  methods: {
    resetData(status) {
      sessionStorage.removeItem("carts");
      this.showLoading(false);
      clearInterval(this.loopPayment);
      if (status) {
        this.$router.push(`/my-cart/checkout/${status}`);
      }
    },
    async getSaleTicketEstimate() { // get sale ticket estimate total figure
      this.showLoading(true);
      // sale ticket payload data
      const payload = {
        branch: this.selectedBranch.id,
        // date: convertDateTimeToUTC({
        //   dateTime: `${this.timeslot.selectedDate} ${this.timeslot.selectedTime}`,
        // }),
        isEstimate: true,
        isOnlinePayment: true,
        items: this.cartsIds,
      };

      try {
        // no need to use saleTicketEstimate API because
        // 1. cart function required user login, not same as booking
        // 2. saleTicket API is more accurate
        const response = await saleTicket(payload);

        this.saleTicketData = response.data;
      } catch (error) {
        console.log(error);
      }

      this.showLoading(false);
    },
    async checkOutHandler() {
      this.showLoading(true);

      const { gateway } = this.paymentMethod;
      const isFiserv =
        gateway?.provider === paymentGatewayProvider.FISERV ? true : false;
      const isStripe =
        gateway?.provider === paymentGatewayProvider.STRIPE ? true : false;

      const payload = {
        branch: this.selectedBranch.id,
        // date: convertDateTimeToUTC({
        //   dateTime: `${this.timeslot.selectedDate} ${this.timeslot.selectedTime}`,
        // }),
        isOnlinePayment: true,
        items: this.cartsIds,
        remark: this.remarks,
        paymentMethod: this.paymentMethod.id
      }

      try {
        // Post sale ticket
        const res = await saleTicket(payload);
        this.saleTicketData = res.data;

        if (isFiserv) {
          const x = window;
          x.open("about:blank", "_target");
          const { date, hash, taxedTotal, token, id } = res.data;
          const datetime = moment(date)
            .utc()
            .format("YYYY:MM:DD-HH:mm:ss")
            .toString();

          sessionStorage.setItem(
            "fiservForm",
            JSON.stringify({
              txndatetime: datetime,
              hash: hash,
              chargetotal: taxedTotal,
              token: token,
              storename: gateway?.key1 ? gateway.key1 : "",
              sharedsecret: gateway?.key2 ? gateway.key2 : "",
            })
          );
          this.fiservPayment = true;
          this.showLoading(true);

          let timeCount = 180;
          this.loopPayment = setInterval(async () => {
            try {
              const res = await getSaleTicket(id);
              const data = res.data;
              if (res.data.status === ticketStatusList.VOIDED) {
                this.resetData("failed");
              }
              if (data.tempPayment !== undefined) {
                if (parseInt(data.tempPayment.paymentAmount || "") > 0) {
                  this.resetData("success");
                  this.showLoading(false);
                }
              } else {
                if (parseInt(data.paymentAmount || "") > 0) {
                  this.resetData("success");
                  this.showLoading(false);
                }
              }
              if (timeCount <= 0) {
                this.resetData("failed");
              }

              timeCount -= 5;
            } catch (error) {
              this.resetData("failed");
              this.showLoading(false);
            }
          }, 5000);
        } else if (isStripe) {
          // Set saleTicket details to sessionstorage
          sessionStorage.setItem("saleTicket", JSON.stringify(res.data));
          sessionStorage.setItem("selectedPaymentMethod", JSON.stringify(this.selectedPaymentMethod));
          // this.$router.push("/my-cart/checkout/online-payment");
          this.$router.push("/my-cart/checkout/online-payment");
        }
      } catch (error) {
        console.log(error);
        this.showLoading(false);
        useToast().error("Oops! Something went wrong. Please try again.");
      }
    },
    removeFromCart(data) {
      return console.log('remove from cart', data);
    },
    viewItemDetail(data) {
      const product = data.product;
      let itemLine = this.getItemLine(product.type, product);
      openModal(
        {
          id: "expandableListItemDetailModal",
        },
        markRaw(defineAsyncComponent(() => import("@/components/ExpandableListItemDetail.vue"))),
        {
          image: product.image,
          processingTime: itemLine.formatProcessingTime,
          name: product.name,
          description: product.description,
          originalPrice: product.originalPrice,
          salePrice: product.salePrice,
          section: product.productSection?.name || '-',
          category: product.productCategory?.name || '-',
          promotion: data.promotion,
        },
        {},
        [
          {
            text: 'Close',
            clickFn: () => {
              closeModal();
            }
          }
        ]
      );
    },
    formatPaymentMethodName(paymentMethod) {
      if (!paymentMethod) {
        return;
      }
      // If payment method is gateway
      if (paymentMethod.type === "GW") {
        // If is Paynow
        if (
          paymentMethod.gateway.provider === paymentGatewayProvider.STRIPE &&
          paymentMethod.gateway.key2 === stripePaymentMethod.PAYNOW
        ) {
          return "PayNow";
        }

        return "Credit / Debit Card";
      }

      // Not a gateway payment
      return paymentMethod.name;
    },
    setSelectedPaymentMethod() {
      // If default selected payment method exists
      if (this.paymentMethod) {
        this.selectedPaymentMethodId = this.paymentMethod.id;
      }
    },
    formatPrice,
    getFormattedNumber,
    getPromotionHeader,
    getItemLine,
    ...mapActions([
      "getCustomerBranches",
      "getEStorePaymentMethods",
    ]),
    ...mapMutations(["showLoading"]),
  },
  watch: {
    selectedPaymentMethod(value) {
      this.$store.commit("setEStorePaymentMethod", value);
    },
  },
  async created() {
    this.setSelectedPaymentMethod();
    if (this.isLoggedIn) {
      this.getCustomerBranches(this.loginUserInfo.id);
    }

    // get user selected carts data from previous page
    this.carts = JSON.parse(sessionStorage.getItem("carts")) || [];

    // Get sale ticket estimate total figure
    this.getSaleTicketEstimate();

    // always refetch estore payment methods instead of storing as cache
    await this.$store.dispatch("getEStorePaymentMethods");
  },
  computed: {
    cartsIds() {
      return this.carts && this.carts.length > 0 ?
        this.carts.map((cart) => {
          return {
            cartId: cart.id,
            promotionId: cart.product.promotion ? cart.product.promotionId : 0,
          }
        }) : [];
    },
    selectedPaymentMethod() {
      return this.paymentMethods.find(
        (method) => method.id === parseInt(this.selectedPaymentMethodId)
      );
    },
    ...mapGetters([
      "init",
      "branchCurrencySymbol",
      "branches",
      "selectedBranch",
      "paymentMethod",
      "loginUserInfo",
    ]),
    ...mapGetters({
      paymentMethod: "eStorePaymentMethod",
      paymentMethods: "eStorePaymentMethods",
    })
  },
};
</script>
<style lang="scss" scoped>
.style-cart-page {
  margin-bottom: 70px;
}
.style-checkbox {
  font-size: 20px;
}
.form-check-input:focus {
  box-shadow: none;
}
.style-product-container {
  border: 1px solid $border-light-grey;
  border-radius: 10px;
}
.style-cart-item-margin {
  width: 90%;
  margin: 0 auto 0 auto;
}
.style-payment-amount-margin {
  width: 95%;
  margin: 0 auto 0 auto;
}
</style>
